import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, concatMap, take, toArray } from 'rxjs/operators';

import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { displayErrorMessage, displaySuccessMessage } from 'ish-core/store/core/messages';

import { SaveForLaterService } from 'src/app/extensions/save-for-later/services/save-for-later/save-for-later.service';
import { loadSaveForLaterSuccess } from 'src/app/extensions/save-for-later/store/save-for-later/save-for-later.actions';

export type PresidioQuoteCartPendingItem = {
  sku: string;
  quantity: number;
  name?: string;
};

export type PresidioQuoteCartPending =
  | { kind: 'catalog_to_sfl'; items: PresidioQuoteCartPendingItem[] }
  | { kind: 'sfl_to_cart_blocked'; sku?: string };

/**
 * CUSTOMIZATION: FR13.7.5 — quote lines and non-quote catalog items cannot share a cart.
 * When a quote is already in the cart, catalog ATC is diverted to Saved for later (confirm modal).
 */
@Injectable({ providedIn: 'root' })
export class PresidioQuoteCartGuardService {
  private checkoutFacade = inject(CheckoutFacade);
  private saveForLaterService = inject(SaveForLaterService);
  private store = inject(Store);

  private hasQuoteInCart = false;
  private readonly pendingSubject = new BehaviorSubject<PresidioQuoteCartPending | null>(null);

  readonly pending$: Observable<PresidioQuoteCartPending | null> = this.pendingSubject.asObservable();

  constructor() {
    this.checkoutFacade.basketLineItems$.subscribe(items => {
      this.hasQuoteInCart = (items || []).some(item => !!item.quote);
    });
  }

  cartContainsQuote(): boolean {
    return this.hasQuoteInCart;
  }

  /**
   * @returns true when the caller should continue adding to the basket;
   * false when the add was diverted to the Saved for later confirm modal.
   */
  tryCatalogAddToBasket(items: PresidioQuoteCartPendingItem[]): boolean {
    const usable = (items || []).filter(item => !!item?.sku && Number(item.quantity) > 0);
    if (!usable.length) {
      return true;
    }
    if (!this.hasQuoteInCart) {
      return true;
    }
    this.pendingSubject.next({ kind: 'catalog_to_sfl', items: usable });
    return false;
  }

  /** Block Move to cart from Saved for later while a quote occupies the cart. */
  tryMoveSaveForLaterToBasket(sku?: string): boolean {
    if (!this.hasQuoteInCart) {
      return true;
    }
    this.pendingSubject.next({ kind: 'sfl_to_cart_blocked', sku });
    return false;
  }

  cancel(): void {
    this.pendingSubject.next(null);
  }

  confirmCatalogToSaveForLater(): void {
    const pending = this.pendingSubject.value;
    if (!pending || pending.kind !== 'catalog_to_sfl') {
      return;
    }
    const items = [...pending.items];
    this.pendingSubject.next(null);

    from(items)
      .pipe(
        concatMap(item =>
          this.saveForLaterService.addSkuToSaveForLater(item.sku, item.quantity).pipe(
            catchError(() => {
              this.store.dispatch(
                displayErrorMessage({ message: 'quote.inbound.catalog_to_sfl.error' })
              );
              return of(null);
            })
          )
        ),
        toArray(),
        take(1)
      )
      .subscribe(results => {
        const last = results.filter(Boolean).pop();
        if (last) {
          this.store.dispatch(loadSaveForLaterSuccess({ saveForLaterList: last }));
          this.store.dispatch(
            displaySuccessMessage({ message: 'quote.inbound.catalog_to_sfl.success' })
          );
        }
      });
  }
}
